import React from "react";
import Plot from "react-plotly.js";
import "./Dashboard.css";

const primary =
  getComputedStyle(document.documentElement).getPropertyValue(
    "--primary-color"
  ) || "#7c3aed";
const gridLight = "#eef2ff";
const gridGray = "#f3f4f6";

function buildLayout(title = "", xTitle = "", yTitle = "") {
  return {
    title,
    template: "plotly_white",
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: "rgba(0,0,0,0)",
    font: {
      family: "Inter, Segoe UI, Roboto, sans-serif",
      size: 12,
      color: "#111827",
    },
    autosize: true,
    height: 620,
    margin: { t: 70, r: 60, b: 120, l: 90 },
    hovermode: "x unified",
    hoverlabel: { bgcolor: "#ffffff", font: { size: 12 } },
    legend: { orientation: "h", y: -0.25 },
    xaxis: {
      title: { text: xTitle },
      showgrid: true,
      gridcolor: gridLight,
      tickangle: -30,
      automargin: true,
    },
    yaxis: {
      title: { text: yTitle },
      showgrid: true,
      gridcolor: gridGray,
      automargin: true,
      rangemode: "tozero",
    },
  };
}

function Chart({ chartData, title }) {
  if (!chartData) return null;
  if (chartData.error) {
    return (
      <div className="chart-error">
        <h3>{title}</h3>
        <p>❌ {chartData.error}</p>
      </div>
    );
  }

  // Plotly string payload
  if (chartData.chart) {
    try {
      const parsed = JSON.parse(chartData.chart);
      return (
        <div className="chart-container">
          <h3>{title}</h3>
          <div className="plot-holder">
            <Plot
              data={parsed.data}
              layout={{
                ...buildLayout(
                  title,
                  parsed?.layout?.xaxis?.title?.text || "",
                  parsed?.layout?.yaxis?.title?.text || ""
                ),
                ...parsed.layout,
              }}
              style={{ width: "100%", height: "100%" }}
              useResizeHandler={true}
              config={{
                responsive: true,
                displaylogo: false,
                modeBarButtonsToRemove: ["autoScale2d"],
              }}
            />
          </div>
        </div>
      );
    } catch (e) {
      return (
        <div className="chart-error">
          <h3>{title}</h3>
          <p>❌ Error parsing chart data: {e.message}</p>
        </div>
      );
    }
  }

  // Chart.js-like payload
  if (chartData.data && chartData.data.datasets) {
    const type = (chartData.type || "").toLowerCase();
    const ds = chartData.data.datasets[0] || {};
    const labels = chartData.data.labels || [];

    // Scatter -> convert {x,y}
    if (type === "scatter") {
      const points = Array.isArray(ds.data) ? ds.data : [];
      const x = points.map((p) =>
        p && typeof p === "object" ? Number(p.x) : null
      );
      const y = points.map((p) =>
        p && typeof p === "object" ? Number(p.y) : null
      );
      const valid = x
        .map((xi, i) => ({ x: xi, y: y[i] }))
        .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
      const xs = valid.map((p) => p.x);
      const ys = valid.map((p) => p.y);

      // Linear regression (least squares)
      const n = xs.length;
      let trendTrace = null;
      if (n >= 2) {
        const sumX = xs.reduce((a, b) => a + b, 0);
        const sumY = ys.reduce((a, b) => a + b, 0);
        const sumXY = xs.reduce((a, b, i) => a + b * ys[i], 0);
        const sumXX = xs.reduce((a, b) => a + b * b, 0);
        const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
        const intercept = sumY / n - (slope * sumX) / n;
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const trendX = [minX, maxX];
        const trendY = trendX.map((vx) => slope * vx + intercept);
        trendTrace = {
          x: trendX,
          y: trendY,
          type: "scatter",
          mode: "lines",
          name: "Trend",
          line: { color: "#10b981", width: 2, dash: "dash" },
          hoverinfo: "skip",
        };
      }
      return (
        <div className="chart-container">
          <h3>{title}</h3>
          <div className="plot-holder">
            <Plot
              data={[
                {
                  x: xs,
                  y: ys,
                  type: "scatter",
                  mode: "markers",
                  marker: {
                    color: ds.borderColor || primary,
                    size: 8,
                    opacity: 0.85,
                    line: { color: "#e5e7eb", width: 0.5 },
                  },
                  hovertemplate: `<b>%{x}</b><br>Value: ₹%{y:,.2f}<extra></extra>`,
                },
                ...(trendTrace ? [trendTrace] : []),
              ]}
              layout={buildLayout(
                title,
                chartData.options?.scales?.x?.title?.text || "Revenue (₹)",
                chartData.options?.scales?.y?.title?.text || "Profit (₹)"
              )}
              style={{ width: "100%", height: "100%" }}
              useResizeHandler={true}
              config={{
                responsive: true,
                displaylogo: false,
                modeBarButtonsToRemove: ["autoScale2d"],
              }}
            />
          </div>
        </div>
      );
    }

    // Pie-like -> show donut
    if (type === "pie") {
      const values = (ds.data || []).map((v) => Number(v));
      return (
        <div className="chart-container">
          <h3>{title}</h3>
          <div className="plot-holder">
            <Plot
              data={[
                {
                  type: "pie",
                  labels,
                  values,
                  hole: 0.35,
                  textinfo: "label+percent",
                  hovertemplate: `<b>%{label}</b><br>₹%{value:,.2f} (%{percent})<extra></extra>`,
                },
              ]}
              layout={buildLayout(title, "", "")}
              style={{ width: "100%", height: "100%" }}
              useResizeHandler={true}
              config={{
                responsive: true,
                displaylogo: false,
                modeBarButtonsToRemove: ["autoScale2d"],
              }}
            />
          </div>
        </div>
      );
    }

    // Line/Bar
    const isLine = type === "line";
    let rawLabels = Array.isArray(labels) ? labels : [];
    let rawValues = Array.isArray(ds.data) ? ds.data : [];
    if (rawLabels.length === 0 && rawValues.length > 0) {
      rawLabels = rawValues.map((_, i) => i);
    }
    const len = Math.min(rawLabels.length, rawValues.length);
    rawLabels = rawLabels.slice(0, len);
    rawValues = rawValues.slice(0, len);
    const topN = isLine
      ? rawLabels.length
      : rawLabels.length > 12
      ? 10
      : rawLabels.length;
    const x = rawLabels.slice(0, topN);
    const y = rawValues.slice(0, topN);

    // moving average for timeseries
    const movingAvg = (arr, window = 7) =>
      arr.map((_, i) => {
        const start = Math.max(0, i - window + 1);
        const slice = arr
          .slice(start, i + 1)
          .filter((v) => typeof v === "number");
        return slice.length
          ? slice.reduce((a, b) => a + b, 0) / slice.length
          : null;
      });

    const traces = [
      {
        x,
        y,
        type: isLine ? "scatter" : "bar",
        mode: isLine ? "lines+markers" : undefined,
        name: ds.label || title,
        line: isLine
          ? {
              color: ds.borderColor || primary,
              shape: "spline",
              smoothing: 0.6,
              width: 3,
            }
          : undefined,
        marker: {
          color: ds.backgroundColor || ds.borderColor || primary,
          line: { color: "#e5e7eb", width: 0 },
        },
        fill: isLine ? "tozeroy" : undefined,
        fillcolor: isLine ? "rgba(124,58,237,0.10)" : undefined,
        hovertemplate: `<b>%{x}</b><br>₹%{y:,.2f}<extra></extra>`,
      },
    ];

    if (isLine) {
      traces.push({
        x,
        y: movingAvg(y, 7),
        type: "scatter",
        mode: "lines",
        name: "7-day Avg",
        line: { color: "#7c3aed", width: 2, dash: "dot" },
        hovertemplate: `<b>%{x}</b><br>7-day Avg: ₹%{y:,.2f}<extra></extra>`,
      });
    }

    // Special handling for hours-of-day labels (0..23)
    const hoursLike =
      x.length > 0 &&
      x.every(
        (v) => Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= 23
      );

    const layout = buildLayout(
      title,
      chartData.options?.scales?.x?.title?.text || "",
      chartData.options?.scales?.y?.title?.text || (isLine ? "₹" : "")
    );
    if (!isLine && hoursLike) {
      layout.xaxis = {
        ...(layout.xaxis || {}),
        type: "category",
        tickmode: "array",
        tickvals: x,
        ticktext: x.map((h) => `${h}:00`),
      };
    }

    return (
      <div className="chart-container">
        <h3>{title}</h3>
        <div className="plot-holder">
          <Plot
            data={traces}
            layout={layout}
            style={{ width: "100%", height: "100%" }}
            useResizeHandler={true}
            config={{
              responsive: true,
              displaylogo: false,
              modeBarButtonsToRemove: ["autoScale2d"],
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="chart-error">
      <h3>{title}</h3>
      <p>❌ Unsupported chart format</p>
    </div>
  );
}

export default Chart;
